import {
  BillingItem,
  BillingTaxTemplate,
  InvoiceItem,
  SalesReceiptItem,
} from '../../types'

export const INVOICE_TAX_RATE = 5

export type InvoiceLineTotals = {
  subtotal: number
  discountAmount: number
  taxableAmount: number
  taxAmount: number
  total: number
}

export type InvoiceTotals = {
  subtotal: number
  totalDiscount: number
  taxableAmount: number
  totalTax: number
  totalAmount: number
}

export function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-ZM', {
    style: 'currency',
    currency: 'ZMW',
  }).format(amount)
}

export function formatSignedCurrency(amount: number) {
  if (amount === 0) return formatCurrency(0)
  return `${amount > 0 ? '+' : '-'}${formatCurrency(Math.abs(amount))}`
}

/**
 * Legacy line calculation retained for non-invoice billing documents that use
 * configurable tax templates.
 */
export function calculateInvoiceLine(item: InvoiceItem): InvoiceLineTotals {
  const subtotal = safeNumber(item.quantity) * safeNumber(item.unitPrice)
  const discountAmount = subtotal * (clampPercentage(item.discount) / 100)
  const taxableAmount = subtotal - discountAmount
  const taxDirection =
    item.taxType === 'Deduction' ? -1 : item.taxType === 'Exempt' ? 0 : 1
  const taxAmount =
    taxableAmount * (clampPercentage(item.tax) / 100) * taxDirection

  return {
    subtotal,
    discountAmount,
    taxableAmount,
    taxAmount,
    total: taxableAmount + taxAmount,
  }
}

/**
 * Invoice line amounts are deliberately pre-tax. Tax is calculated once on
 * the invoice's combined taxable amount to prevent compounding or per-line
 * rounding differences.
 */
export function calculateInvoicePreTaxLine(
  item: InvoiceItem,
): InvoiceLineTotals {
  const subtotal = safeNumber(item.quantity) * safeNumber(item.unitPrice)
  const discountAmount = subtotal * (clampPercentage(item.discount) / 100)
  const taxableAmount = subtotal - discountAmount

  return {
    subtotal,
    discountAmount,
    taxableAmount,
    taxAmount: 0,
    total: taxableAmount,
  }
}

export function recalculateInvoiceItem(item: InvoiceItem): InvoiceItem {
  const normalized = {
    ...item,
    quantity: Math.max(0, safeNumber(item.quantity)),
    unitPrice: Math.max(0, safeNumber(item.unitPrice)),
    discount: clampPercentage(item.discount),
    tax: INVOICE_TAX_RATE,
    taxProfileId: 'invoice-tot-tax-5',
    taxProfileName: 'TOT Tax',
    taxType: 'Charge' as const,
  }
  return {
    ...normalized,
    total: calculateInvoicePreTaxLine(normalized).total,
  }
}

/**
 * Applies the invoice policy in order: subtotal, discount, taxable amount,
 * one 5% tax line (rounded half-up to two decimals), then total.
 */
export function calculateInvoiceTotals(items: InvoiceItem[]): InvoiceTotals {
  const preTaxTotals = items.reduce(
    (totals, item) => {
      const line = calculateInvoicePreTaxLine(item)
      return {
        subtotal: totals.subtotal + line.subtotal,
        totalDiscount: totals.totalDiscount + line.discountAmount,
      }
    },
    { subtotal: 0, totalDiscount: 0 },
  )

  return calculateAggregateTotals(preTaxTotals)
}

export function calculateSalesReceiptTotals(
  items: SalesReceiptItem[],
): InvoiceTotals {
  const subtotal = items.reduce(
    (total, item) => total + safeNumber(item.quantity) * safeNumber(item.rate),
    0,
  )

  return calculateAggregateTotals({ subtotal, totalDiscount: 0 })
}

export function buildInvoiceItem(
  billingItem: BillingItem,
  taxTemplate: BillingTaxTemplate,
  options: {
    description?: string
    quantity?: number
  } = {},
): InvoiceItem {
  return recalculateInvoiceItem({
    id: `inv-item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    itemId: billingItem.id,
    serviceName: billingItem.serviceName,
    description: options.description ?? billingItem.description ?? '',
    quantity: options.quantity ?? 1,
    unitPrice: billingItem.unitPrice,
    discount: 0,
    discountEligible: billingItem.discountEligibility,
    tax: taxTemplate.rate,
    taxProfileId: taxTemplate.id,
    taxProfileName: taxTemplate.name,
    taxType: taxTemplate.type,
    total: 0,
  })
}

export function applyTaxTemplate(
  item: InvoiceItem,
  taxTemplate: BillingTaxTemplate,
): InvoiceItem {
  return recalculateInvoiceItem({
    ...item,
    tax: taxTemplate.rate,
    taxProfileId: taxTemplate.id,
    taxProfileName: taxTemplate.name,
    taxType: taxTemplate.type,
  })
}

export function getTaxDisplayName(item: InvoiceItem) {
  if (item.taxProfileName) return item.taxProfileName
  if (item.taxType === 'Deduction') return 'Medical Scheme deduction'
  if (item.taxType === 'Exempt' || item.tax === 0) return 'Tax Exempt'
  return 'Tax'
}

export function getTaxRateLabel(item: InvoiceItem) {
  const typeLabel =
    item.taxType === 'Deduction'
      ? 'deduction'
      : item.taxType === 'Exempt'
        ? 'exempt'
        : 'charge'
  return `${safeNumber(item.tax)}% ${typeLabel}`
}

function calculateAggregateTotals(input: {
  subtotal: number
  totalDiscount: number
}): InvoiceTotals {
  const taxableAmount = input.subtotal - input.totalDiscount
  const totalTax = roundHalfUpToTwoDecimals(
    taxableAmount * (INVOICE_TAX_RATE / 100),
  )

  return {
    ...input,
    taxableAmount,
    totalTax,
    totalAmount: taxableAmount + totalTax,
  }
}

function roundHalfUpToTwoDecimals(value: number) {
  return Math.floor((value + Number.EPSILON) * 100 + 0.5) / 100
}

function safeNumber(value: number) {
  return Number.isFinite(value) ? value : 0
}

function clampPercentage(value: number) {
  return Math.min(100, Math.max(0, safeNumber(value)))
}
